import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Navbar from '../../components/navbar/navbar.jsx'
import apinode from '../../services/apinode.js'

const BRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(v) || 0)

const fmtDate = (d) => {
  if (!d) return '—'
  try {
    const [ano, mes, dia] = d.substring(0, 10).split('-')
    return `${dia}/${mes}/${ano}`
  } catch { return d }
}

const SITUACAO_CONFIG = {
  'Recebido':  { color: '#10b981', bg: '#d1fae5', label: 'Recebido'  },
  'Pago':      { color: '#10b981', bg: '#d1fae5', label: 'Pago'       },
  'Em Aberto': { color: '#6366f1', bg: '#e0e7ff', label: 'Em Aberto' },
  'Em aberto': { color: '#6366f1', bg: '#e0e7ff', label: 'Em aberto' },
  'Parcial':   { color: '#f59e0b', bg: '#fef3c7', label: 'Parcial'   },
  'Vencido':   { color: '#ef4444', bg: '#fee2e2', label: 'Vencido'   },
  'Cancelado': { color: '#ef4444', bg: '#fee2e2', label: 'Cancelado'  },
  'Atendido':  { color: '#10b981', bg: '#d1fae5', label: 'Atendido'   },
}

const getSituacao = (sit) =>
  SITUACAO_CONFIG[sit] || { color: '#6b7280', bg: '#f3f4f6', label: sit }

const isAdmin = () => localStorage.getItem('admin') === 'SIM'

export default function Caixa() {
  const navigate       = useNavigate()
  const [searchParams] = useSearchParams()
  const inputRef       = useRef(null)

  const [numeroPedido, setNumeroPedido] = useState('')
  const [buscando, setBuscando]         = useState(false)
  const [infoPedido, setInfoPedido]     = useState(null)
  const [parcelas,   setParcelas]       = useState([])
  const [dadosCaixa, setDadosCaixa]     = useState(null)

  const [selecionadas,     setSelecionadas]     = useState({})
  const [valoresRecebidos, setValoresRecebidos] = useState({})
  const [formasParcela,    setFormasParcela]    = useState({})

  const [caixasAbertos,      setCaixasAbertos]      = useState([])
  const [idCaixaSelecionado, setIdCaixaSelecionado] = useState('')
  const [trocoGeral,         setTrocoGeral]         = useState(0)
  const [isFinalizando,      setIsFinalizando]      = useState(false)
  const [vencimentosSaldo,   setVencimentosSaldo]   = useState({})

  // ── Admin: busca de caixas por data ────────────────────────────────────────
  const hoje = new Date().toISOString().substring(0, 10)
  const [adminDataInicio, setAdminDataInicio] = useState(hoje)
  const [adminDataFim,    setAdminDataFim]    = useState(hoje)
  const [buscandoCaixas,  setBuscandoCaixas]  = useState(false)
  const [abaAtiva,        setAbaAtiva]        = useState('pedido') // 'pedido' | 'caixas'

  // ── carrega caixas + lê ?pedido= da URL ────────────────────────────────────
  useEffect(() => {
    carregarCaixas()
    const pedidoUrl = searchParams.get('pedido')
    if (pedidoUrl) {
      setNumeroPedido(pedidoUrl)
      buscarPedidoComNumero(pedidoUrl)
    } else {
      inputRef.current?.focus()
    }
  }, [])

  // Admin vê todos os caixas (abertos + fechados), outros só os abertos
  const carregarCaixas = async () => {
    try {
      if (isAdmin()) {
        const r = await apinode.get(`pedido/listacaixas/${localStorage.idconta}`)
        setCaixasAbertos(r.data)
      } else {
        const r = await apinode.get(`pedido/listacaixaaberto/${localStorage.idconta}`)
        setCaixasAbertos(r.data)
      }
    } catch {
      try {
        const r = await apinode.get(`pedido/listacaixaaberto/${localStorage.idconta}`)
        setCaixasAbertos(r.data)
      } catch {}
    }
  }

  // ── Admin: buscar caixas por data ──────────────────────────────────────────
  const buscarCaixasPorData = async (e) => {
    e?.preventDefault()
    if (!adminDataInicio || !adminDataFim) return
    setBuscandoCaixas(true)
    try {
      const r = await apinode.get(
        `pedido/listacaixaspordata/${localStorage.idconta}/${adminDataInicio}/${adminDataFim}`
      )
      setCaixasAbertos(r.data)
      if (r.data.length === 0) toast.info('Nenhum caixa encontrado no período.')
      else toast.success(`${r.data.length} caixa(s) encontrado(s).`)
    } catch {
      toast.error('Erro ao buscar caixas por data!')
    } finally {
      setBuscandoCaixas(false)
    }
  }

  // ── função central de busca (aceita número direto) ─────────────────────────
  const buscarPedidoComNumero = async (num) => {
    if (!num) return
    setBuscando(true)
    setInfoPedido(null)
    setParcelas([])
    setDadosCaixa(null)
    setSelecionadas({})
    setValoresRecebidos({})
    setFormasParcela({})
    setTrocoGeral(0)
    try {
      const [resPedido, resParcelas] = await Promise.all([
        apinode.get(`/pedido/pedido-editar/${num}/${localStorage.idconta}`),
        apinode.get(`/pedido/parcelas/${num}/${localStorage.idconta}`),
      ])
      const ped = resPedido.data.dadospedido
      setInfoPedido(ped)
      setParcelas(resParcelas.data)
      const vals  = {}
      const formas = {}
      resParcelas.data.forEach((p) => {
        vals[p.id]   = p.valor
        formas[p.id] = p.forma_de_pgto || ''
      })
      setValoresRecebidos(vals)
      setFormasParcela(formas)
      try {
        const resCaixa = await apinode.get(`/pedido/conferecaixa/${num}/${localStorage.idconta}`)
        setDadosCaixa(resCaixa.data)
      } catch {}
    } catch {
      toast.error('Pedido não encontrado!')
    } finally {
      setBuscando(false)
    }
  }

  const buscarPedido = async (e) => {
    e?.preventDefault()
    const num = numeroPedido.trim()
    if (!num) return
    await buscarPedidoComNumero(num)
  }

  // ── seleção ────────────────────────────────────────────────────────────────
  const toggleParcela = (id, situacao) => {
    if (situacao === 'Recebido') return
    setSelecionadas(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const toggleTodas = () => {
    const abertas  = parcelas.filter(p => p.situacao !== 'Recebido')
    const todasSel = abertas.every(p => selecionadas[p.id])
    const novo     = {}
    abertas.forEach(p => { novo[p.id] = !todasSel })
    setSelecionadas(novo)
  }

  const parcelasSelecionadas = parcelas.filter(p => selecionadas[p.id])

  // ── totais ─────────────────────────────────────────────────────────────────
  const totalSelecionado = parcelasSelecionadas.reduce(
    (acc, p) => acc + parseFloat(valoresRecebidos[p.id] || p.valor), 0
  )
  const totalPendente = parcelas
    .filter(p => p.situacao !== 'Recebido')
    .reduce((acc, p) => acc + parseFloat(p.valor), 0)
  const totalRecebido = parcelas
    .filter(p => p.situacao === 'Recebido')
    .reduce((acc, p) => acc + parseFloat(p.vlr_recebido || p.valor), 0)

  // ── finalizar ──────────────────────────────────────────────────────────────
  const handleFinalizar = async () => {
    if (isFinalizando) return
    if (parcelasSelecionadas.length === 0) { toast.warn('Selecione ao menos uma parcela!'); return }
    if (!idCaixaSelecionado)               { toast.warn('Selecione o caixa!'); return }

    const semForma = parcelasSelecionadas.filter(p => !formasParcela[p.id])
    if (semForma.length > 0) {
      toast.warn(`Selecione a forma de pagamento para ${semForma.length} parcela(s)!`)
      return
    }

    const comSaldo = parcelasSelecionadas.filter(
      p => parseFloat(valoresRecebidos[p.id]) < parseFloat(p.valor) - 0.009
    )

    const vencSaldoFinal = { ...vencimentosSaldo }
    for (const p of comSaldo) {
      const saldo = parseFloat(p.valor) - parseFloat(valoresRecebidos[p.id])
      const resposta = window.prompt(
        `Parcela ${p.numero_doc} — Saldo de ${BRL(saldo)} será gerado como nova parcela.\n\nInforme o vencimento (YYYY-MM-DD) ou deixe em branco para manter o mesmo:`,
        vencSaldoFinal[p.id] || p.vencimento?.substring(0, 10) || ''
      )
      if (resposta === null) return
      vencSaldoFinal[p.id] = resposta.trim() || p.vencimento?.substring(0, 10)
    }
    setVencimentosSaldo(vencSaldoFinal)

    const caixaSel    = caixasAbertos.find(c => String(c.id_cx) === String(idCaixaSelecionado))
    const caixaFechado = !!caixaSel?.data_fechamento

    if (!window.confirm(
      `Confirmar recebimento de ${parcelasSelecionadas.length} parcela(s) — Total: ${BRL(totalSelecionado)}?${
        comSaldo.length > 0 ? `\n\n${comSaldo.length} saldo(s) serão gerados como nova(s) parcela(s).` : ''
      }${caixaFechado ? '\n\n⚠️ ATENÇÃO: O caixa selecionado está FECHADO. O lançamento será registrado retroativamente.' : ''}`
    )) return

    try {
      setIsFinalizando(true)
      await apinode.post(
        `/pedido/baixar-parcelas/${numeroPedido.trim()}/${localStorage.idconta}`,
        {
          id_caixa:       idCaixaSelecionado,
          id_cliente:     infoPedido.id_cliente,
          forcar_fechado: isAdmin() ? true : undefined,
          parcelas: parcelasSelecionadas.map(p => ({
            id:               p.id,
            numero_ident:     p.numero_ident,
            numero_doc:       p.numero_doc,
            valor_original:   parseFloat(p.valor),
            vlr_recebido:     parseFloat(valoresRecebidos[p.id] || p.valor),
            forma_pgto:       formasParcela[p.id],
            troco:            parseFloat(trocoGeral) || 0,
            vencimento_saldo: vencSaldoFinal[p.id] || null,
          })),
        }
      )
      toast.success('Parcelas baixadas com sucesso!')
      setSelecionadas({})
      setVencimentosSaldo({})
      setTrocoGeral(0)
      await buscarPedidoComNumero(numeroPedido.trim())
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao baixar parcelas!')
    } finally {
      setIsFinalizando(false)
    }
  }

  const pedidoQuitado = parcelas.length > 0 && parcelas.every(p => p.situacao === 'Recebido')

  // ── caixa selecionado está fechado? ────────────────────────────────────────
  const caixaFechadoSelecionado =
    idCaixaSelecionado &&
    !!caixasAbertos.find(c => String(c.id_cx) === String(idCaixaSelecionado))?.data_fechamento

  return (
    <>
      <Navbar />

      <style>{`
        .cx-root{min-height:100vh;background:linear-gradient(135deg,#f8fafc,#e2e8f0);padding:24px 16px;font-family:'Segoe UI',sans-serif;}
        .cx-header{background:linear-gradient(135deg,#4f46e5,#7c3aed);border-radius:16px;padding:24px 32px;color:white;margin-bottom:24px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:16px;}
        .cx-header h1{margin:0;font-size:1.6rem;font-weight:700;}
        .cx-card{background:white;border-radius:14px;box-shadow:0 4px 20px rgba(0,0,0,.07);overflow:hidden;margin-bottom:20px;}
        .cx-card-header{background:linear-gradient(135deg,#f8fafc,#e2e8f0);padding:14px 20px;border-bottom:1px solid #e5e7eb;font-weight:600;font-size:.9rem;color:#374151;display:flex;align-items:center;gap:8px;}
        .cx-card-body{padding:20px;}
        .cx-search-form{display:flex;gap:10px;}
        .cx-input{flex:1;border:2px solid #e5e7eb;border-radius:10px;padding:12px 16px;font-size:15px;font-weight:600;letter-spacing:1px;outline:none;transition:border-color .2s;}
        .cx-input:focus{border-color:#4f46e5;}
        .cx-input-sm{border:1.5px solid #e5e7eb;border-radius:8px;padding:6px 10px;font-size:13px;font-weight:600;outline:none;transition:border-color .2s;width:110px;text-align:right;}
        .cx-input-sm:focus{border-color:#4f46e5;}
        .cx-select-sm{border:1.5px solid #e5e7eb;border-radius:8px;padding:6px 10px;font-size:12px;outline:none;background:white;min-width:130px;}
        .cx-select-sm:focus{border-color:#4f46e5;}
        .cx-select-lg{width:100%;border:2px solid #e5e7eb;border-radius:10px;padding:11px 14px;font-size:14px;outline:none;background:white;transition:border-color .2s;}
        .cx-select-lg:focus{border-color:#4f46e5;}
        .cx-btn{border:none;border-radius:10px;padding:12px 22px;font-weight:600;font-size:14px;cursor:pointer;transition:all .2s;display:flex;align-items:center;gap:8px;}
        .cx-btn:disabled{opacity:.55;cursor:not-allowed;transform:none!important;}
        .cx-btn-primary{background:linear-gradient(135deg,#4f46e5,#7c3aed);color:white;}
        .cx-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 4px 15px rgba(79,70,229,.3);}
        .cx-btn-success{background:linear-gradient(135deg,#10b981,#059669);color:white;}
        .cx-btn-success:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 4px 15px rgba(16,185,129,.3);}
        .cx-btn-outline{background:white;border:2px solid #e5e7eb;color:#374151;}
        .cx-btn-sm{padding:6px 14px;font-size:12px;border-radius:8px;}
        .cx-table{width:100%;border-collapse:collapse;font-size:13px;}
        .cx-table th{background:#f8fafc;padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;font-weight:600;border-bottom:2px solid #e5e7eb;white-space:nowrap;}
        .cx-table td{padding:10px 12px;border-bottom:1px solid #f3f4f6;color:#374151;vertical-align:middle;}
        .cx-table tr:last-child td{border-bottom:none;}
        .cx-table tr.selecionada td{background:#eff6ff;}
        .cx-table tr.recebida{opacity:.65;}
        .cx-badge{display:inline-flex;align-items:center;padding:3px 10px;border-radius:99px;font-size:11px;font-weight:700;white-space:nowrap;}
        .cx-totais-bar{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px;}
        .cx-total-card{background:white;border-radius:10px;padding:14px;border:1px solid #e5e7eb;text-align:center;box-shadow:0 2px 8px rgba(0,0,0,.04);}
        .cx-total-card label{display:block;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#9ca3af;font-weight:700;margin-bottom:4px;}
        .cx-total-card span{font-size:17px;font-weight:800;}
        .cx-resumo{background:linear-gradient(135deg,#4f46e5,#7c3aed);color:white;border-radius:12px;padding:18px;margin-bottom:14px;}
        .cx-resumo h3{margin:0 0 10px;font-size:.95rem;opacity:.85;}
        .cx-resumo-row{display:flex;justify-content:space-between;align-items:flex-start;padding:5px 0;border-bottom:1px solid rgba(255,255,255,.15);font-size:13px;}
        .cx-resumo-row:last-child{border-bottom:none;}
        .cx-resumo-total{font-size:1.3rem;font-weight:800;}
        .cx-empty{text-align:center;padding:48px 20px;color:#9ca3af;}
        .cx-empty i{font-size:2.5rem;display:block;margin-bottom:10px;}
        .cx-spinner{display:inline-block;width:15px;height:15px;border:2px solid rgba(255,255,255,.4);border-top-color:white;border-radius:50%;animation:spin .7s linear infinite;}
        @keyframes spin{to{transform:rotate(360deg);}}
        .cx-label{display:block;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#9ca3af;font-weight:700;margin-bottom:6px;}
        .cx-info-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;}
        .cx-info-item label{display:block;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#9ca3af;font-weight:700;margin-bottom:3px;}
        .cx-info-item span{font-size:14px;font-weight:700;color:#111827;}
        .cx-abat{background:#fef3c7;border:1px solid #fcd34d;border-radius:10px;padding:10px 14px;margin-top:12px;font-size:13px;color:#92400e;display:flex;align-items:center;gap:8px;}
        .cx-link{color:#4f46e5;font-size:13px;cursor:pointer;text-decoration:underline;background:none;border:none;padding:0;margin-left:auto;}
        input[type=checkbox]{width:16px;height:16px;cursor:pointer;accent-color:#4f46e5;}

        /* Tabs admin */
        .cx-tabs{display:flex;gap:0;margin-bottom:20px;background:white;border-radius:12px;padding:4px;box-shadow:0 2px 10px rgba(0,0,0,.06);width:fit-content;}
        .cx-tab{border:none;background:none;padding:9px 20px;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer;color:#6b7280;transition:all .18s;display:flex;align-items:center;gap:7px;}
        .cx-tab.ativo{background:linear-gradient(135deg,#4f46e5,#7c3aed);color:white;box-shadow:0 2px 10px rgba(79,70,229,.3);}

        /* Painel admin busca por data */
        .cx-admin-panel{background:linear-gradient(135deg,#1e1b4b,#312e81);border-radius:14px;padding:20px 24px;margin-bottom:20px;color:white;}
        .cx-admin-panel-title{font-size:.85rem;font-weight:700;text-transform:uppercase;letter-spacing:.08em;opacity:.7;margin-bottom:14px;display:flex;align-items:center;gap:8px;}
        .cx-admin-form{display:flex;gap:12px;align-items:flex-end;flex-wrap:wrap;}
        .cx-admin-field{display:flex;flex-direction:column;gap:5px;}
        .cx-admin-field label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;opacity:.65;}
        .cx-admin-input{border:2px solid rgba(255,255,255,.2);background:rgba(255,255,255,.1);border-radius:9px;padding:9px 13px;font-size:14px;font-weight:600;color:white;outline:none;transition:border-color .2s;}
        .cx-admin-input:focus{border-color:rgba(255,255,255,.6);}
        .cx-admin-input::-webkit-calendar-picker-indicator{filter:invert(1);opacity:.7;cursor:pointer;}
        .cx-btn-admin{background:rgba(255,255,255,.15);border:2px solid rgba(255,255,255,.25);border-radius:9px;padding:9px 20px;font-weight:700;font-size:13px;cursor:pointer;color:white;transition:all .2s;display:flex;align-items:center;gap:7px;}
        .cx-btn-admin:hover:not(:disabled){background:rgba(255,255,255,.25);}
        .cx-btn-admin:disabled{opacity:.5;cursor:not-allowed;}
        .cx-badge-fechado{background:#fee2e2;color:#dc2626;font-size:10px;padding:2px 8px;border-radius:99px;font-weight:700;}
        .cx-badge-aberto{background:#d1fae5;color:#059669;font-size:10px;padding:2px 8px;border-radius:99px;font-weight:700;}
        .cx-aviso-fechado{margin-top:6px;background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;padding:7px 12px;font-size:12px;color:#92400e;display:flex;align-items:center;gap:6px;}

        @media(max-width:900px){
          .cx-main-grid{grid-template-columns:1fr!important;}
          .cx-totais-bar{grid-template-columns:1fr 1fr!important;}
          .cx-admin-form{flex-direction:column;}
          .cx-admin-input{width:100%;}
        }
      `}</style>

      <div className="cx-root">
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>

          {/* Header */}
          <div className="cx-header">
            <div>
              <h1><i className="bi bi-cash-register me-2"></i>Caixa PDV</h1>
              <p>&nbsp;</p>
            </div>
            <button className="cx-btn cx-btn-outline" onClick={() => navigate(-1)}>
              <i className="bi bi-arrow-left"></i> Voltar
            </button>
          </div>

          {/* Tabs — apenas para admin */}
          {isAdmin() && (
            <div className="cx-tabs">
              <button
                className={`cx-tab ${abaAtiva === 'pedido' ? 'ativo' : ''}`}
                onClick={() => setAbaAtiva('pedido')}
              >
                <i className="bi bi-search"></i> Buscar Pedido
              </button>
              <button
                className={`cx-tab ${abaAtiva === 'caixas' ? 'ativo' : ''}`}
                onClick={() => setAbaAtiva('caixas')}
              >
                <i className="bi bi-calendar3"></i> Caixas por Data
              </button>
            </div>
          )}

          {/* ── ABA: Caixas por Data (admin) ─────────────────────────────── */}
          {isAdmin() && abaAtiva === 'caixas' && (
            <>
              <div className="cx-admin-panel">
                <div className="cx-admin-panel-title">
                  <i className="bi bi-shield-lock-fill"></i>
                  Administrador — Consulta de Caixas por Período
                </div>
                <form className="cx-admin-form" onSubmit={buscarCaixasPorData}>
                  <div className="cx-admin-field">
                    <label>Data Início</label>
                    <input
                      type="date"
                      className="cx-admin-input"
                      value={adminDataInicio}
                      onChange={e => setAdminDataInicio(e.target.value)}
                      required
                    />
                  </div>
                  <div className="cx-admin-field">
                    <label>Data Fim</label>
                    <input
                      type="date"
                      className="cx-admin-input"
                      value={adminDataFim}
                      onChange={e => setAdminDataFim(e.target.value)}
                      required
                    />
                  </div>
                  <button type="submit" className="cx-btn-admin" disabled={buscandoCaixas}>
                    {buscandoCaixas
                      ? <><span className="cx-spinner" style={{ borderColor: 'rgba(255,255,255,.3)', borderTopColor: 'white' }} /> Buscando...</>
                      : <><i className="bi bi-search"></i> Buscar Caixas</>}
                  </button>
                  <button
                    type="button"
                    className="cx-btn-admin"
                    style={{ opacity: .75 }}
                    onClick={() => { setAdminDataInicio(hoje); setAdminDataFim(hoje); carregarCaixas() }}
                  >
                    <i className="bi bi-arrow-clockwise"></i> Hoje
                  </button>
                </form>
              </div>

              <div className="cx-card">
                <div className="cx-card-header">
                  <i className="bi bi-cash-stack"></i>
                  Caixas encontrados ({caixasAbertos.length})
                </div>
                {caixasAbertos.length === 0 ? (
                  <div className="cx-empty">
                    <i className="bi bi-inbox"></i>
                    Nenhum caixa encontrado. Refine o período e busque novamente.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="cx-table">
                      <thead>
                        <tr>
                          <th>Nº Caixa</th>
                          <th>Data Abertura</th>
                          <th>Data Fechamento</th>
                          <th>Fundo</th>
                          <th>Status</th>
                          <th>Operador</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {caixasAbertos.map((c) => (
                          <tr key={c.id_cx}>
                            <td style={{ fontWeight: 700 }}>#{c.id_cx}</td>
                            <td>{c.data_abertura ? new Date(c.data_abertura).toLocaleString('pt-BR') : '—'}</td>
                            <td>{c.data_fechamento ? new Date(c.data_fechamento).toLocaleString('pt-BR') : '—'}</td>
                            <td style={{ fontWeight: 700 }}>{BRL(c.fundo_caixa)}</td>
                            <td>
                              {c.data_fechamento
                                ? <span className="cx-badge-fechado">🔒 Fechado</span>
                                : <span className="cx-badge-aberto">✅ Aberto</span>}
                            </td>
                            <td style={{ fontSize: 12, color: '#6b7280' }}>{c.operador || '—'}</td>
                            <td>
                              <button
                                className="cx-btn cx-btn-outline cx-btn-sm"
                                onClick={() => {
                                  setIdCaixaSelecionado(String(c.id_cx))
                                  setAbaAtiva('pedido')
                                  toast.info(`Caixa #${c.id_cx} selecionado!`)
                                }}
                              >
                                <i className="bi bi-check2"></i> Usar este
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── ABA: Buscar Pedido ────────────────────────────────────────── */}
          {abaAtiva === 'pedido' && (
            <>
              {/* Busca */}
              <div className="cx-card">
                <div className="cx-card-header">
                  <i className="bi bi-search"></i> Buscar Pedido
                </div>
                <div className="cx-card-body">
                  <form className="cx-search-form" onSubmit={buscarPedido}>
                    <input
                      ref={inputRef}
                      type="text"
                      className="cx-input"
                      placeholder="Digite o número do pedido..."
                      value={numeroPedido}
                      onChange={(e) => setNumeroPedido(e.target.value)}
                    />
                    <button
                      type="submit"
                      className="cx-btn cx-btn-primary"
                      disabled={buscando || !numeroPedido.trim()}
                    >
                      {buscando
                        ? <><span className="cx-spinner" /> Buscando...</>
                        : <><i className="bi bi-search" /> Buscar</>}
                    </button>
                  </form>
                </div>
              </div>

              {buscando && !infoPedido && (
                <div className="cx-card">
                  <div className="cx-empty" style={{ padding: '40px 20px' }}>
                    <span className="cx-spinner" style={{ width: 30, height: 30, borderWidth: 3, borderColor: 'rgba(79,70,229,.2)', borderTopColor: '#4f46e5' }} />
                    <p style={{ color: '#6b7280', marginTop: 16 }}>Carregando pedido...</p>
                  </div>
                </div>
              )}

              {infoPedido && (
                <>
                  {/* Info pedido */}
                  <div className="cx-card">
                    <div className="cx-card-header">
                      <i className="bi bi-receipt"></i>
                      Pedido #{numeroPedido} — {infoPedido.cliente}
                      <button className="cx-link" onClick={() => navigate(`/pedido/editar/${numeroPedido}`)}>
                        Abrir pedido ↗
                      </button>
                    </div>
                    <div className="cx-card-body">
                      <div className="cx-info-grid">
                        <div className="cx-info-item">
                          <label>Valor Total</label>
                          <span>{BRL(infoPedido.valor)}</span>
                        </div>
                        <div className="cx-info-item">
                          <label>Tipo</label>
                          <span>{infoPedido.tipo_pedido || '—'}</span>
                        </div>
                        <div className="cx-info-item">
                          <label>Status</label>
                          <span>
                            {(() => {
                              const s = getSituacao(infoPedido.status)
                              return <span className="cx-badge" style={{ background: s.bg, color: s.color }}>{infoPedido.status || 'Em aberto'}</span>
                            })()}
                          </span>
                        </div>
                      </div>
                      {parseFloat(infoPedido.valor_devolvido) > 0 && (
                        <div className="cx-abat">
                          <i className="bi bi-arrow-down-circle-fill"></i>
                          <span>
                            <strong>Abatimento:</strong>{' '}
                            <span style={{ color: '#dc2626', fontWeight: 700 }}>- {BRL(infoPedido.valor_devolvido)}</span>
                            {' '}→ Valor líquido: <strong>{BRL(parseFloat(infoPedido.valor) - parseFloat(infoPedido.valor_devolvido))}</strong>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Totais */}
                  {parcelas.length > 0 && (
                    <div className="cx-totais-bar">
                      <div className="cx-total-card">
                        <label>Total Parcelas</label>
                        <span>{BRL(parcelas.reduce((a, p) => a + parseFloat(p.valor), 0))}</span>
                      </div>
                      <div className="cx-total-card">
                        <label>Já Recebido</label>
                        <span style={{ color: '#10b981' }}>{BRL(totalRecebido)}</span>
                      </div>
                      <div className="cx-total-card">
                        <label>Pendente</label>
                        <span style={{ color: totalPendente > 0 ? '#ef4444' : '#10b981' }}>{BRL(totalPendente)}</span>
                      </div>
                      <div className="cx-total-card">
                        <label>Selecionado</label>
                        <span style={{ color: '#4f46e5' }}>{BRL(totalSelecionado)}</span>
                      </div>
                    </div>
                  )}

                  {/* Grid principal */}
                  <div className="cx-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20, alignItems: 'start' }}>

                    {/* Tabela parcelas */}
                    <div className="cx-card">
                      <div className="cx-card-header">
                        <i className="bi bi-list-check"></i>
                        Parcelas ({parcelas.length})
                        {parcelas.some(p => p.situacao !== 'Recebido') && (
                          <button className="cx-btn cx-btn-outline cx-btn-sm" style={{ marginLeft: 'auto' }} onClick={toggleTodas}>
                            {parcelas.filter(p => p.situacao !== 'Recebido').every(p => selecionadas[p.id])
                              ? 'Desmarcar todas' : 'Selecionar abertas'}
                          </button>
                        )}
                      </div>

                      {parcelas.length === 0 ? (
                        <div className="cx-empty">
                          <i className="bi bi-inbox"></i>
                          Nenhuma parcela encontrada.
                          <br /><small>Lance as contas a receber no pedido primeiro.</small>
                        </div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table className="cx-table">
                            <thead>
                              <tr>
                                <th style={{ width: 36 }}>
                                  <input
                                    type="checkbox"
                                    onChange={toggleTodas}
                                    checked={
                                      parcelas.filter(p => p.situacao !== 'Recebido').length > 0 &&
                                      parcelas.filter(p => p.situacao !== 'Recebido').every(p => selecionadas[p.id])
                                    }
                                  />
                                </th>
                                <th>Nº</th>
                                <th>Vencimento</th>
                                <th>Valor</th>
                                <th>Situação</th>
                                <th>Forma Pgto</th>
                                <th>Vlr Recebido</th>
                                <th>Dt Recebto</th>
                              </tr>
                            </thead>
                            <tbody>
                              {parcelas.map((p) => {
                                const recebida = p.situacao === 'Recebido'
                                const sel      = !!selecionadas[p.id]
                                const sit      = getSituacao(p.situacao)
                                const vencida  = !recebida && p.vencimento && new Date(p.vencimento) < new Date()
                                const vlrRec   = parseFloat(valoresRecebidos[p.id] ?? p.valor)
                                const temSaldo = sel && vlrRec < parseFloat(p.valor) - 0.009

                                return (
                                  <tr
                                    key={p.id}
                                    className={sel ? 'selecionada' : recebida ? 'recebida' : ''}
                                    style={{ cursor: recebida ? 'default' : 'pointer' }}
                                    onClick={() => !recebida && toggleParcela(p.id, p.situacao)}
                                  >
                                    <td onClick={e => e.stopPropagation()}>
                                      <input
                                        type="checkbox"
                                        checked={sel}
                                        disabled={recebida}
                                        onChange={() => toggleParcela(p.id, p.situacao)}
                                      />
                                    </td>
                                    <td style={{ fontWeight: 700 }}>{p.numero_doc}</td>
                                    <td>
                                      <span style={{ color: vencida ? '#ef4444' : 'inherit', fontWeight: vencida ? 600 : 400 }}>
                                        {fmtDate(p.vencimento)}
                                      </span>
                                      {vencida && <span style={{ fontSize: 10, display: 'block', color: '#ef4444', fontWeight: 700 }}>VENCIDA</span>}
                                    </td>
                                    <td style={{ fontWeight: 700 }}>{BRL(p.valor)}</td>
                                    <td>
                                      <span className="cx-badge" style={{ background: sit.bg, color: sit.color }}>
                                        {sit.label}
                                      </span>
                                    </td>
                                    <td onClick={e => e.stopPropagation()}>
                                      {sel ? (
                                        <select
                                          className="cx-select-sm"
                                          value={formasParcela[p.id] || ''}
                                          onChange={e => setFormasParcela(prev => ({ ...prev, [p.id]: e.target.value }))}
                                        >
                                          <option value="">Selecione...</option>
                                          <option value="Dinheiro">Dinheiro</option>
                                          <option value="Cartão">Cartão</option>
                                          <option value="Pix">Pix</option>
                                          <option value="Boleto">Boleto</option>
                                          <option value="Cheque">Cheque</option>
                                          <option value="Devolucao">Devolução</option>
                                          <option value="Haver">Haver</option>
                                        </select>
                                      ) : (
                                        <span style={{ color: '#6b7280', fontSize: 12 }}>{p.forma_de_pgto || '—'}</span>
                                      )}
                                    </td>
                                    <td onClick={e => e.stopPropagation()}>
                                      {sel ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                          <input
                                            type="number"
                                            className="cx-input-sm"
                                            value={valoresRecebidos[p.id] ?? p.valor}
                                            step="0.01"
                                            min="0.01"
                                            onChange={e => setValoresRecebidos(prev => ({ ...prev, [p.id]: e.target.value }))}
                                          />
                                          {temSaldo && (
                                            <span title={`Saldo gerado: ${BRL(p.valor - vlrRec)}`} style={{ color: '#f59e0b', fontSize: 16 }}>⚠️</span>
                                          )}
                                        </div>
                                      ) : (
                                        <span style={{ color: recebida ? '#10b981' : '#9ca3af', fontWeight: recebida ? 700 : 400 }}>
                                          {recebida ? BRL(p.vlr_recebido || p.valor) : '—'}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ fontSize: 12, color: '#6b7280' }}>
                                      {fmtDate(p.data_recebimento)}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Painel confirmação */}
                    <div>
                      <div className="cx-card">
                        <div className="cx-card-header">
                          <i className="bi bi-check2-circle"></i>
                          Confirmar Recebimento
                        </div>
                        <div className="cx-card-body">
                          {pedidoQuitado ? (
                            <div className="cx-empty" style={{ padding: '28px 10px' }}>
                              <i className="bi bi-check-circle-fill" style={{ color: '#10b981' }}></i>
                              <p style={{ color: '#10b981', fontWeight: 700, margin: '10px 0' }}>Pedido totalmente quitado!</p>
                              <button
                                className="cx-btn cx-btn-outline cx-btn-sm"
                                onClick={() => {
                                  setNumeroPedido('')
                                  setInfoPedido(null)
                                  setParcelas([])
                                  setTimeout(() => inputRef.current?.focus(), 100)
                                }}
                              >
                                <i className="bi bi-search"></i> Novo pedido
                              </button>
                            </div>
                          ) : (
                            <>
                              {parcelasSelecionadas.length > 0 ? (
                                <div className="cx-resumo">
                                  <h3><i className="bi bi-list-ul me-1"></i>Resumo da baixa</h3>
                                  {parcelasSelecionadas.map(p => {
                                    const vlr   = parseFloat(valoresRecebidos[p.id] || p.valor)
                                    const saldo = parseFloat(p.valor) - vlr
                                    return (
                                      <div className="cx-resumo-row" key={p.id}>
                                        <span>Parcela {p.numero_doc}</span>
                                        <div style={{ textAlign: 'right' }}>
                                          <div style={{ fontWeight: 700 }}>{BRL(vlr)}</div>
                                          {saldo > 0.009 && <div style={{ fontSize: 11, opacity: .75 }}>saldo gerado: {BRL(saldo)}</div>}
                                        </div>
                                      </div>
                                    )
                                  })}
                                  <div className="cx-resumo-row" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,.3)' }}>
                                    <span style={{ fontWeight: 700 }}>Total</span>
                                    <span className="cx-resumo-total">{BRL(totalSelecionado)}</span>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13 }}>
                                  <i className="bi bi-hand-index me-1"></i> Selecione as parcelas na tabela
                                </div>
                              )}

                              {/* Select caixa */}
                              <div style={{ marginBottom: 14 }}>
                                <label className="cx-label">
                                  Caixa
                                  {isAdmin() && (
                                    <span style={{ marginLeft: 6, color: '#7c3aed', fontWeight: 700 }}>
                                      · admin: abertos e fechados
                                    </span>
                                  )}
                                </label>
                                <select
                                  className="cx-select-lg"
                                  value={idCaixaSelecionado}
                                  onChange={e => setIdCaixaSelecionado(e.target.value)}
                                  style={caixaFechadoSelecionado ? { borderColor: '#f59e0b', background: '#fffbeb' } : {}}
                                >
                                  <option value="">Selecione o caixa...</option>
                                  {caixasAbertos.map(c => {
                                    const fechado = !!c.data_fechamento
                                    return (
                                      <option key={c.id_cx} value={c.id_cx}>
                                        {fechado ? '🔒 ' : '✅ '}Caixa Nº {c.id_cx} — Fundo: {BRL(c.fundo_caixa)}{fechado ? ' [FECHADO]' : ''}
                                      </option>
                                    )
                                  })}
                                </select>
                                {caixaFechadoSelecionado && (
                                  <div className="cx-aviso-fechado">
                                    <i className="bi bi-exclamation-triangle-fill"></i>
                                    Caixa fechado selecionado. O lançamento será registrado retroativamente.
                                  </div>
                                )}
                              </div>

                              <div style={{ marginBottom: 16 }}>
                                <label className="cx-label">Troco (se houver)</label>
                                <input
                                  type="number"
                                  className="cx-select-lg"
                                  style={{ fontWeight: 600 }}
                                  placeholder="0,00"
                                  value={trocoGeral}
                                  min={0}
                                  step="0.01"
                                  onChange={e => setTrocoGeral(e.target.value)}
                                />
                              </div>

                              <button
                                className="cx-btn cx-btn-success"
                                style={{ width: '100%', justifyContent: 'center', padding: '14px', fontSize: 15 }}
                                onClick={handleFinalizar}
                                disabled={isFinalizando || parcelasSelecionadas.length === 0 || !idCaixaSelecionado}
                              >
                                {isFinalizando
                                  ? <><span className="cx-spinner" /> Processando...</>
                                  : <><i className="bi bi-check-circle"></i> Baixar {parcelasSelecionadas.length > 0 ? `${parcelasSelecionadas.length} Parcela(s)` : ''}</>
                                }
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {dadosCaixa?.historico?.length > 0 && (
                        <div className="cx-card">
                          <div className="cx-card-header">
                            <i className="bi bi-clock-history"></i> Movimentos no Caixa
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                            <table className="cx-table">
                              <thead>
                                <tr>
                                  <th>Data</th>
                                  <th>Forma</th>
                                  <th>Recebido</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dadosCaixa.historico.map((h, i) => (
                                  <tr key={i}>
                                    <td style={{ fontSize: 11 }}>{h.data ? new Date(h.data).toLocaleString('pt-BR') : '—'}</td>
                                    <td><span className="cx-badge" style={{ background: '#e0e7ff', color: '#4f46e5' }}>{h.forma_pgto}</span></td>
                                    <td style={{ color: '#10b981', fontWeight: 700 }}>{BRL(h.vlr_recebido)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {!infoPedido && !buscando && (
                <div className="cx-card">
                  <div className="cx-empty" style={{ padding: '60px 20px' }}>
                    <i className="bi bi-cash-register" style={{ fontSize: '3rem', color: '#c4b5fd' }}></i>
                    <p style={{ color: '#6b7280', marginTop: 12 }}>Digite o número de um pedido para começar</p>
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </>
  )
}